import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { VisitSurface } from "@backstory/protocol";
import { api, queryKeys } from "../api";
import { PlayerView } from "./PlayerView";
import { Skeleton } from "./States";
import { STATUS_LABELS, statusAt, surfaceLabel, type SurfaceStateChange, type SurfaceStatus } from "./surfaces";

export interface SurfacePaneProps {
  surface: VisitSurface;
  /** Shared visit clock in absolute epoch ms. */
  nowAbs: number;
  /** Mirrors the primary transport so both panes move together. */
  playing: boolean;
  states?: SurfaceStateChange[];
  /** Status reconciled across the visit, so the pane and the rail agree. */
  status?: SurfaceStatus;
  /** Move the visit clock, used by the "jump there" affordances. */
  onJumpTo?: (absTs: number) => void;
  /** Make this surface the primary pane (transport, DevTools, citations). */
  onPromote?: () => void;
}

/**
 * A second surface replayed beside the primary one, read-only and locked to the
 * same visit clock. When this surface has nothing to show at the current moment
 * the pane says which of the reasons applies instead of going blank.
 */
export function SurfacePane({ surface, nowAbs, playing, states, status, onJumpTo, onPromote }: SurfacePaneProps) {
  const own = useMemo(() => statusAt(surface, nowAbs, states), [surface, nowAbs, states]);
  const moment = useMemo(() => ({ ...own, status: status ?? own.status }), [own, status]);
  const segmentId = moment.segment?.id ?? "";
  const covered = moment.covered;
  const detail = useQuery({
    queryKey: queryKeys.session(segmentId),
    queryFn: () => api.session(segmentId),
    enabled: !!segmentId && covered,
  });
  const fetchChunk = useCallback((seq: number) => api.chunk(segmentId, seq), [segmentId]);
  const label = `${surfaceLabel(surface)} · ${STATUS_LABELS[moment.status]}`;

  return (
    <div className="surface-pane" data-testid={`surface-pane-${surface.surfaceId}`}>
      <header>
        <span className="muted small" title={surface.url}>{label}</span>
        {onPromote && (
          <button className="btn sm ghost" onClick={onPromote} title="Drive playback, events and DevTools from this surface">
            Make primary
          </button>
        )}
      </header>
      {!covered ? (
        <div className="surface-pane-note" data-testid="surface-pane-note">
          <strong>{STATUS_LABELS[moment.status]}</strong>
          <span className="muted small">
            {moment.status === "before" && "This surface had not opened yet at this point in the visit."}
            {moment.status === "closed" && "This surface was closed or stopped recording before this point."}
            {moment.status === "gap" && "Nothing was recorded on this surface at this moment."}
          </span>
          {moment.segment && onJumpTo && (
            <button className="btn sm" onClick={() => onJumpTo(new Date(moment.segment!.startedAt).getTime() + moment.offsetMs)}>
              {moment.status === "closed" ? "Jump to its last moment" : "Jump to when it was recording"}
            </button>
          )}
        </div>
      ) : detail.isPending ? (
        <Skeleton lines={4} />
      ) : detail.isError ? (
        <div className="surface-pane-note">
          <strong>Could not load this surface</strong>
          <span className="muted small">{(detail.error as Error).message}</span>
        </div>
      ) : (
        <PlayerView
          refs={detail.data.chunks}
          fetchChunk={fetchChunk}
          sessionKey={segmentId}
          privacyMode={detail.data.privacyMode}
          controls={false}
          shortcuts={false}
          readOnly
          syncAbs={nowAbs}
          syncPlaying={playing}
        />
      )}
    </div>
  );
}
